/** Project detail: the gate, the scores, status, and closure.
 *
 * Shown as a page of its own — at `/projects/<id>`, and in a separate browser window when
 * a project is opened from the list (Brian, 2026-10-13: the side drawer felt like "some
 * weird half window"). A window of its own can sit beside the list or on a second screen,
 * and its address can be pasted to somebody.
 *
 * The reason and closure fields are required by the API. They are marked required here
 * too so the user learns it before submitting rather than from a rejection — but the
 * server check is the one that counts.
 */

import { useEffect, useState } from 'react'
import { ArrowLeft, Check, Pencil, X } from 'lucide-react'

import {
  Button,
  Can,
  ErrorState,
  Field,
  InfoDot,
  Loading,
  Pill,
  SectionHeader,
  inputClass,
} from '@/components/ui'
import {
  STAGES,
  onHoldNote,
  ragTone,
  stageDescription,
  stageLabel,
  statusLabel,
  statusTone,
} from '@/lib/domain'
import type { Project } from '@/lib/domain'
import { Permissions } from '@/lib/session'
import { CharterSections } from '@/features/documents/CharterSections'
import { KeyDocuments } from '@/features/documents/KeyDocuments'
import { Assignments, Dependencies, Milestones } from '@/features/delivery/ProjectDelivery'
import { ClosureRecord } from '@/features/projects/ClosureRecord'
import { DeleteProject, ProjectStatusControl } from '@/features/projects/ProjectAdminActions'
import { NotificationSettings } from '@/features/projects/NotificationSettings'
import { ProjectSchedule } from '@/features/projects/ProjectSchedule'
import { ProjectWigCard } from '@/features/wig/ProjectWigCard'
import {
  useCloseProject,
  useProject,
  useScoreProject,
  useSetPercentComplete,
  useStageHistory,
  useTransitionStage,
  useUpdateProject,
} from '@/features/projects/useProjects'

/** The drawer's title, with rename behind the pencil.
 *
 * Inline rather than a dialog: a rename is one field, and the title is where the eye
 * already is. Hidden for mirrored projects — their name follows the Azure DevOps epic
 * and the server would refuse anyway. A duplicate name comes back as the server's own
 * conflict message; the check that counts lives there.
 */
function ProjectTitle({ project }: { project: Project | null }) {
  const rename = useUpdateProject(project?.id ?? '')
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')

  if (!project) return <h2 className="font-heading text-2xl text-fg">Project</h2>

  const save = () => {
    const trimmed = name.trim()
    if (!trimmed || trimmed === project.name) {
      setEditing(false)
      return
    }
    rename.mutate(
      { name: trimmed, row_version: project.rowVersion },
      { onSuccess: () => setEditing(false) },
    )
  }

  if (editing) {
    return (
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <input
            className={`${inputClass} font-heading text-xl`}
            value={name}
            autoFocus
            aria-label="Project name"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') save()
              // stopPropagation so the drawer's Escape-to-close leaves the edit open;
              // first Escape cancels the rename, a second closes the drawer.
              if (event.key === 'Escape') {
                event.stopPropagation()
                setEditing(false)
              }
            }}
          />
          <Button onClick={save} disabled={rename.isPending} title="Save the new name">
            <Check size={14} aria-hidden />
          </Button>
          <Button onClick={() => setEditing(false)} title="Cancel">
            <X size={14} aria-hidden />
          </Button>
        </div>
        {rename.error ? (
          <p className="mt-1 text-xs" style={{ color: 'var(--status-red)' }}>
            {rename.error instanceof Error ? rename.error.message : 'The rename failed.'}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      <h2 className="truncate font-heading text-2xl text-fg">{project.name}</h2>
      {!project.isExternal ? (
        <Can permission={Permissions.projectsEdit}>
          <button
            type="button"
            className="rounded-md p-1 text-fg-muted hover:bg-surface hover:text-fg"
            aria-label="Rename this project"
            title="Rename this project"
            onClick={() => {
              setName(project.name)
              rename.reset()
              setEditing(true)
            }}
          >
            <Pencil size={14} aria-hidden />
          </button>
        </Can>
      ) : null}
    </div>
  )
}

function ScoreSelect({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: number | null
  onChange: (value: number | null) => void
  hint: string
}) {
  return (
    <Field label={label} hint={hint}>
      <select
        className={inputClass}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value ? Number(event.target.value) : null)}
      >
        <option value="">Unscored</option>
        {[1, 2, 3, 4, 5].map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </Field>
  )
}


/** Percent complete: normally rolled up from the work items, sometimes typed.
 *
 * The figure is derived for most projects and wrong for some — work waiting on a vendor,
 * or a phase tracked somewhere else, is real progress no work item represents. While an
 * override is in force the rollup leaves it alone, so it is worth saying on screen which
 * of the two is showing; otherwise nobody can tell why a number is or is not moving.
 */
function PercentComplete({ project }: { project: Project }) {
  const set = useSetPercentComplete()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(String(project.percentComplete))

  if (!editing) {
    return (
      <div>
        <dt className="micro-label text-fg-muted">Complete</dt>
        <dd className="flex flex-wrap items-center gap-2 text-fg">
          {project.percentComplete}%
          {project.percentIsManual ? (
            <Pill>Set by hand</Pill>
          ) : (
            <span className="text-[11px] text-fg-muted">from work items</span>
          )}
          <Can permission={Permissions.projectsEdit}>
            <button
              type="button"
              className="text-[11px] underline text-fg-muted hover:text-fg"
              onClick={() => {
                setValue(String(project.percentComplete))
                setEditing(true)
              }}
            >
              Change
            </button>
          </Can>
        </dd>
      </div>
    )
  }

  return (
    <div className="col-span-2">
      <Field label="Complete" hint="Leave the rollup in charge by releasing it back.">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={0}
            max={100}
            className={`${inputClass} w-24`}
            value={value}
            autoFocus
            onChange={(event) => setValue(event.target.value)}
          />
          <Button
            variant="primary"
            disabled={set.isPending}
            onClick={() =>
              set.mutate(
                {
                  id: project.id,
                  percent: Number(value),
                  rowVersion: project.rowVersion,
                },
                { onSuccess: () => setEditing(false) },
              )
            }
          >
            {set.isPending ? 'Saving…' : 'Set'}
          </Button>
          {project.percentIsManual ? (
            <Button
              variant="secondary"
              disabled={set.isPending}
              title="Hand this back to the rollup; it recomputes straight away."
              onClick={() =>
                set.mutate(
                  { id: project.id, percent: null, rowVersion: project.rowVersion },
                  { onSuccess: () => setEditing(false) },
                )
              }
            >
              Release
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      </Field>
      {set.error ? <ErrorState error={set.error} /> : null}
    </div>
  )
}



/** How this project came to be in the stage it is in.
 *
 * Worth stating because a stage can change without anybody choosing it: when the first work
 * item goes In Progress, the rollup advances the project to Execution & Transition and
 * records itself as the actor. That is correct — it stops the board and the stage drifting
 * apart — but somebody opening the project afterwards finds it somewhere they did not put
 * it, with nothing on screen to explain why.
 */
function StageProvenance({ projectId, stage }: { projectId: string; stage: string }) {
  const { data } = useStageHistory(projectId)
  const latest = (data?.history ?? []).find((move) => move.stage === stage)

  if (!latest) {
    return (
      <p className="text-xs text-fg-muted">
        Currently at <strong className="text-fg">{stageLabel(stage)}</strong>.
      </p>
    )
  }

  return (
    <p className="text-xs text-fg-muted">
      Currently at <strong className="text-fg">{stageLabel(stage)}</strong> —{' '}
      {latest.automatic ? (
        <>
          moved here automatically when work started
          {latest.reason ? ` (${latest.reason})` : ''}
        </>
      ) : (
        <>
          moved by {latest.actor}
          {latest.reason ? `: ${latest.reason}` : ''}
        </>
      )}{' '}
      on {new Date(latest.occurredAt).toLocaleDateString()}.
    </p>
  )
}


export function ProjectDetail({
  id,
  inWindow,
  onClose,
}: {
  id: string
  /** Opened as its own browser window from the list, rather than navigated to. */
  inWindow: boolean
  onClose: () => void
}) {
  const { data: project, isPending, error } = useProject(id)

  const transition = useTransitionStage(id)
  const close = useCloseProject(id)
  const score = useScoreProject(id)

  const [targetStage, setTargetStage] = useState('')
  const [reason, setReason] = useState('')
  const [summary, setSummary] = useState('')
  const [handoff, setHandoff] = useState('')
  const [scores, setScores] = useState<{ value: number | null; risk: number | null; effort: number | null }>({
    value: null,
    risk: null,
    effort: null,
  })

  useEffect(() => {
    if (!project) return
    setScores({
      value: project.scores.value,
      risk: project.scores.risk,
      effort: project.scores.effort,
    })
    setTargetStage('')
    setReason('')
  }, [project])

  // The window's title is the project's, so a taskbar full of them can be told apart.
  useEffect(() => {
    if (project) document.title = `${project.name} · Wheeler PMT`
    return () => {
      document.title = 'Wheeler PMT'
    }
  }, [project])

  const mutationError = transition.error ?? close.error ?? score.error

  return (
    <div className="mx-auto max-w-[1400px] pt-2">
      <article aria-label="Project detail">
        <div className="mb-4 flex items-start justify-between gap-4">
          <ProjectTitle project={project ?? null} />
          <div className="flex shrink-0 items-center gap-1">
            {inWindow ? (
              <Button onClick={onClose} title="Close this window">
                <X size={14} aria-hidden /> Close window
              </Button>
            ) : (
              <Button onClick={onClose} title="Back to the project list">
                <ArrowLeft size={14} aria-hidden /> All projects
              </Button>
            )}
          </div>
        </div>

        {isPending ? (
          <Loading />
        ) : error ? (
          <ErrorState error={error} />
        ) : !project ? null : (
          <div className="space-y-6">
            {mutationError ? <ErrorState error={mutationError} /> : null}

            {/* Two columns when expanded: the project itself on the left, the documents and
                the decisions about it on the right. Narrow, the two simply stack. */}
            <div className="grid items-start gap-6 lg:grid-cols-2">
              <div className="min-w-0 space-y-6">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone={statusTone(project.status)}>
                    {statusLabel(project.status)}
                    {onHoldNote(project) ? ` ${onHoldNote(project)}` : ''}
                  </Pill>
                  {project.ragStatus !== 'none' && (
                    <Pill tone={ragTone(project.ragStatus)}>{project.ragStatus}</Pill>
                  )}
                  <Pill>{stageLabel(project.stage)}</Pill>
                  <Pill>{project.teamShortLabel}</Pill>
                  {/* Mirrors an Azure DevOps epic: the work under it is read-only here, and
                      the name/team/category are managed by the sync. The link answers "where
                      do I actually edit this?" before anyone has to find out from a 400. */}
                  {project.isExternal ? (
                    project.externalUrl ? (
                      <a
                        href={project.externalUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="micro-label underline"
                        style={{ color: 'var(--status-info)' }}
                        title="This project mirrors an Azure DevOps epic. Its work is managed there."
                      >
                        ADO epic ↗
                      </a>
                    ) : (
                      <Pill>ADO</Pill>
                    )
                  ) : null}
                </div>

                <ProjectWigCard
                  projectId={project.id}
                  isWig={project.isWig}
                  isLive={['not_started', 'active', 'on_hold'].includes(project.status)}
                />

                {project.description ? (
                  <p className="text-sm text-fg-muted">{project.description}</p>
                ) : null}

                <dl className="card grid grid-cols-2 gap-3 p-4 text-sm">
                  <PercentComplete project={project} />
                  {[
                    ['Owner', project.ownerName || '—'],
                    [
                      'Priority',
                      project.scores.priority === null ? 'Unscored' : String(project.scores.priority),
                    ],
                    ['Last change', `${project.modifiedBy}`],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="micro-label text-fg-muted">{label}</dt>
                      <dd className="text-fg">{value}</dd>
                    </div>
                  ))}
                </dl>

                {/* Dates live here rather than in the grid above, because they now carry a
                    history and a reason box — a slip record is not a field. */}
                <ProjectSchedule project={project} />

                <section>
                  <Milestones projectId={id} />
                </section>

                <section>
                  <Dependencies projectId={id} />
                </section>

                <section>
                  <Assignments projectId={id} />
                </section>

                <NotificationSettings projectId={id} />
              </div>

              <div className="min-w-0 space-y-6">
                <Can permission={Permissions.documentsView}>
                  {/* Sections first: they are what the charter is generated *from*, so writing
                      them before pressing Generate on the document is the order that works. */}
                  <CharterSections projectId={id} />
                  <KeyDocuments projectId={id} />
                </Can>

                <Can permission={Permissions.prioritiesScore}>
                  <section>
                    <SectionHeader>Prioritisation</SectionHeader>
                    <div className="card space-y-3 p-4">
                      <div className="grid grid-cols-3 gap-3">
                        <ScoreSelect
                          label="Value"
                          value={scores.value}
                          hint="5 = most"
                          onChange={(value) => setScores((s) => ({ ...s, value }))}
                        />
                        <ScoreSelect
                          label="Risk"
                          value={scores.risk}
                          hint="5 = most"
                          onChange={(risk) => setScores((s) => ({ ...s, risk }))}
                        />
                        <ScoreSelect
                          label="Effort"
                          value={scores.effort}
                          hint="5 = most"
                          onChange={(effort) => setScores((s) => ({ ...s, effort }))}
                        />
                      </div>
                      <p className="text-xs text-fg-muted">
                        Priority is (Value + Risk) ÷ Effort. All three are needed before a project
                        can be ranked.
                      </p>
                      <Button
                        variant="primary"
                        disabled={score.isPending}
                        onClick={() =>
                          score.mutate({ ...scores, row_version: project.rowVersion })
                        }
                      >
                        {score.isPending ? 'Saving…' : 'Save scores'}
                      </Button>
                    </div>
                  </section>
                </Can>

                <Can permission={Permissions.projectsTransitionStage}>
                  <section>
                    <SectionHeader>Phase gate</SectionHeader>
                    <div className="card space-y-3 p-4">
                      <StageProvenance projectId={project.id} stage={project.stage} />
                      <Field
                        label={
                          <span className="inline-flex items-center gap-1.5">
                            Move to
                            <InfoDot label="What each phase means">
                              <span className="block font-semibold text-fg">The four phases</span>
                              {STAGES.map((s) => (
                                <span key={s.value} className="mt-2 block">
                                  <span className="font-semibold text-fg">{s.label}</span>
                                  {s.value === project.stage ? ' — current' : ''}
                                  <br />
                                  {s.description}
                                </span>
                              ))}
                            </InfoDot>
                          </span>
                        }
                      >
                        <select
                          className={inputClass}
                          value={targetStage}
                          onChange={(event) => setTargetStage(event.target.value)}
                        >
                          <option value="">Select a stage</option>
                          {/* Every stage, in the order they run, with the current one shown and
                              disabled rather than removed.
                          
                              Filtering it out was the old behaviour and it read as a bug: a
                              project already at Execution & Transition offered Initiation,
                              Planning & Design and Closure, so the stage you were in looked
                              like the one stage you could not pick. A control that silently
                              omits your current state gives you no way to tell where you are. */}
                          {STAGES.map((s) => (
                            <option key={s.value} value={s.value} disabled={s.value === project.stage}>
                              {s.label}
                              {s.value === project.stage ? ' — current' : ''}
                            </option>
                          ))}
                        </select>
                      </Field>
                      {/* The meaning of whatever is selected — or of the current stage before a
                          choice is made. Reading it beside the name is what stops a stage being
                          picked because its label sounded closest. */}
                      <p className="-mt-1 text-xs text-fg-muted">
                        {stageDescription(targetStage || project.stage)}
                      </p>
                      <Field
                        label="Reason (required)"
                        hint="Recorded in the audit trail against your name."
                      >
                        <textarea
                          className={inputClass}
                          rows={2}
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                        />
                      </Field>
                      <Button
                        variant="primary"
                        disabled={!targetStage || reason.trim().length < 3 || transition.isPending}
                        onClick={() =>
                          transition.mutate({
                            target_stage: targetStage,
                            reason,
                            row_version: project.rowVersion,
                          })
                        }
                      >
                        {transition.isPending ? 'Moving…' : 'Move stage'}
                      </Button>
                    </div>
                  </section>
                </Can>

                <ProjectStatusControl key={project.rowVersion} project={project} />

                {project.status !== 'complete' && (
                  <Can permission={Permissions.projectsClose}>
                    <section>
                      <SectionHeader>Closure</SectionHeader>
                      <div className="card space-y-3 p-4">
                        <Field label="Closure summary (required)">
                          <textarea
                            className={inputClass}
                            rows={2}
                            value={summary}
                            onChange={(event) => setSummary(event.target.value)}
                          />
                        </Field>
                        <Field
                          label="Runbook or handoff (required)"
                          hint="Name the document, or state in one line why none is needed."
                        >
                          <textarea
                            className={inputClass}
                            rows={2}
                            value={handoff}
                            onChange={(event) => setHandoff(event.target.value)}
                          />
                        </Field>
                        <Button
                          variant="primary"
                          disabled={
                            summary.trim().length < 10 ||
                            handoff.trim().length < 10 ||
                            close.isPending
                          }
                          onClick={() =>
                            close.mutate({
                              summary,
                              handoff_note: handoff,
                              row_version: project.rowVersion,
                            })
                          }
                        >
                          {close.isPending ? 'Closing…' : 'Close project'}
                        </Button>
                      </div>
                    </section>
                  </Can>
                )}

                {project.status === 'complete' ? (
                  <section>
                    <ClosureRecord project={project} />
                  </section>
                ) : null}

                <DeleteProject project={project} onDeleted={onClose} />
              </div>
            </div>
          </div>
        )}
      </article>
    </div>
  )
}
